import { observer } from "mobx-react";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { AppBar } from "../../components-v2/AppBar";
import { useAppBar } from "../../appBar/useAppBar";
import { TabProps, Tabs } from "../../components-v2/Tabs";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { Dialog } from "../../components-v2/Dialog";
import { Language } from "../../i18n/languages";
import { Checkbox } from "../../components-v2/Checkbox";
import {
  useDispatch,
  useLanguage,
  usePortfolioStore,
  useRootStore,
  useTranslation,
} from "../../stores/RootStoreContext";
import { Box, Card, Container, Grid, GridItem, VBox } from "../../components-v2/Box";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { CompanyItem, isOk, isPending } from "../../common/common";
import {
  AddAdditionalCompanyForm,
  AdminDialogStore,
  ContactCompany,
  ContactRow,
  EditEmailForm,
  filterAdminRows,
  InviteNewAdminDialogStore,
  prepareAndFilterContactRows,
  prepareRowData,
  RevokeAccessForm,
  RevokeAllForm,
} from "./contactsDomain";
import { HoverTooltip } from "../../components-v2/Tooltip";
import { formatPercent } from "../../utils/numbers";
import { NamedOrganisationNumber } from "../../types/Transactions";
import { isMobileView } from "../../utils/screen";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect, Select } from "../../components-v2/Select";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { DropdownMenu, DropdownMenuItemType } from "../../components-v2/Dropdown";
import { navigateToAdminCompanyPortfolios, navigateToAdminDashboard } from "../../utils/navigation";
import { AdminItem } from "../../types/Settings";
import {
  extractPortfolioAdminsUrl,
  extractPortfolioContactsAdminUrl,
  extractPortfolioContactsUrl,
  extractPortfolioShareholderUrl,
  PortfolioType,
  SelectedPortfolioOption,
  usePortfolio,
} from "../portfolioDomain";
import { PortfolioCompany } from "../../stores/PortfolioStore";
import { ExtendedShareholderDTO, transformShareholder } from "../../types/Shareholder";
import { EmptyState } from "../../components-v2/EmptyState";
import { SendInvitationDialog } from "../../views/Users/SendInvitationDialog";
import { navigate } from "../../events/eventsDomain";
import { LastLogin } from "../../common/commonComponents";
import { RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import {
  prepShareholderStatusFilter,
  ShareholderStatus,
} from "../../shareholderRegister/shareholderRegisterDomain";
import { toDate } from "../../utils/Dates";
import { Alert } from "../../components-v2/Alert";
import { Markdown } from "../../text/Markdown";
import { FileUploadConfig, getSupportedExtensions } from "../../fileUpload/fileUploadDomain";
import { CustomRoleItem } from "../../common/customRole";
import { CustomRoleView } from "./customRoleComponents";
import {
  CustomRoleStore,
  getRoleNameByLanguage,
  prepCustomRoleUsersTableData,
} from "./customRoleDomain";

type PortfolioContactsTab =
  | { type: "shareholders" }
  | { type: "admins" }
  | {
      type: "custom-role";
      customRoleId: string;
    };

const mapToContactsTab = (customRoleId: string, tab?: string): PortfolioContactsTab => {
  switch (tab) {
    case "admins":
      return { type: "admins" };
    case "custom-role":
      return { type: "custom-role", customRoleId };
    default:
      return { type: "shareholders" };
  }
};

export const PortfolioContactsPage = observer(function PortfolioContactsPage({
  portfolioType,
  tab,
  portfolioId,
  customRoleId,
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  tab?: string;
  customRoleId?: string;
}) {
  const { portfolioStore, messageStore, dispatch, apiService, userStore } = useRootStore();
  const id = customRoleId ?? "";
  const locale = useLanguage();
  const contactsTab = mapToContactsTab(id, tab);
  const { t } = useTranslation();
  const { portfolio } = usePortfolio({
    activeHeader: "contacts",
    portfolioId,
    portfolioType,
  });

  useEffect(() => {
    portfolioStore.fetchPortfolioShareholders(portfolio);
    portfolioStore.fetchPortfolioAdmins(portfolio);
    portfolioStore.initCompanyInAdminsFilter();
    portfolioStore.fetchPortfolioCustomRole(portfolio, portfolioId);
    return () => {
      portfolioStore.resetPortfolioShareholders();
      portfolioStore.resetPortfolioAdmins();
      portfolioStore.resetPortfolioCustomRole();
    };
  }, [portfolioStore, portfolio, portfolioId]);

  const appBar = useAppBar({
    isPortfolioPage: true,
    portfolioHeader: "contacts",
    selectedPortfolioOption: portfolio,
  });
  const adminsResult = portfolioStore.fetchPortfolioAdminsResult;
  const admins = isOk(adminsResult) ? adminsResult.data.admins : [];

  const customRoleItemResult = portfolioStore.fetchPortfolioCustomRoleResult;
  const customRoleItems = isOk(customRoleItemResult)
    ? customRoleItemResult.data.customRoleItems
    : [];
  const result = portfolioStore.fetchPortfolioShareholdersResult;

  const store = useMemo(() => {
    if (isOk(customRoleItemResult) && customRoleId !== undefined) {
      const { data } = customRoleItemResult;
      const item = data.customRoleItems.find((r) => r.role.customRoleId === customRoleId);
      if (item) {
        const creatorCompany = userStore.companiesWithAdminAccess.find(
          (adminCompany) => adminCompany.organisationNumber === item.role.orgNo,
        );
        return new CustomRoleStore({
          customRoleItem: item,
          availableCompanies: data.availableCompanies,
          dispatch: dispatch,
          apiService,
          selectedPortfolioOption: portfolio,
          locale,
          showEmailField: true,
          user: userStore.user,
          refreshUserInformation: userStore.refreshUserInformation,
          creatorCompany,
        });
      }
    }
  }, [
    customRoleItemResult,
    customRoleId,
    dispatch,
    apiService,
    portfolio,
    locale,
    userStore.user,
    userStore.refreshUserInformation,
    userStore.companiesWithAdminAccess,
  ]);

  function renderShareholders() {
    if (result === undefined || isPending(result)) {
      return (
        <VBox fullheight justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    } else if (isOk(result)) {
      const shareholders = result.data.shareholders.map((s) => transformShareholder(s));
      return (
        <ContactsView
          shareholders={shareholders}
          shareholderNameFilter={portfolioStore.shareholderNameFilter}
          companyFilter={portfolioStore.companyFilter}
          shareholderStatusFilter={portfolioStore.shareholderStatusFilter}
          setShareholderStatusFilter={portfolioStore.setShareholderStatusFilter}
          allCompanies={portfolioStore.allCompanies}
          onNavigateToShareholder={(shareholderId) =>
            dispatch([
              {
                type: "navigate",
                url: extractPortfolioShareholderUrl(portfolio, shareholderId),
              },
            ])
          }
          downloadShareholdersExcelFile={() =>
            portfolioStore.downloadShareholdersExcelFile(portfolio)
          }
          uploadShareholderExcelForUpdate={(files: File[] | null) =>
            portfolioStore.uploadShareholderExcelForUpdate(files, portfolio)
          }
          importExcelConfig={portfolioStore.importExcelConfig}
          isSuperAdmin={portfolioStore.isSuperAdmin}
        />
      );
    } else {
      return undefined;
    }
  }

  function renderAdmins() {
    if (adminsResult === undefined || isPending(adminsResult)) {
      return (
        <VBox fullheight justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    } else if (isOk(adminsResult)) {
      const companyItems = adminsResult.data.availableCompanies;
      return (
        <>
          {adminsResult.data.admins.length > 0 ? (
            <AdminsView
              admins={adminsResult.data.admins}
              adminsSearch={portfolioStore.adminNameFilter}
              companyFilter={portfolioStore.companyInAdminsFilter}
              openInviteNewAdminDialog={(lang) =>
                dispatch([
                  {
                    type: "showInviteNewAdminDialog",
                    lang,
                    companyItems,
                  },
                ])
              }
              navigateToAdminView={(externalId) =>
                dispatch([
                  {
                    type: "navigate",
                    url: extractPortfolioAdminsUrl(portfolio, externalId),
                  },
                ])
              }
              openEmailEdit={(externalId, email, adminName) =>
                portfolioStore.openEditEmailDialog({
                  id: externalId,
                  email,
                  onSuccess: (updatedEmail) => {
                    const index = adminsResult.data.admins.findIndex((e) => e.id === externalId);
                    adminsResult.data.admins[index].email = updatedEmail;
                  },
                  name: adminName,
                })
              }
              openRevokeAll={(orgNos, externalId, name) =>
                portfolioStore.openRevokeAllDialog({
                  orgNos,
                  externalId,
                  name,
                  onSuccess: () => {
                    portfolioStore.onRevokeAllAdminAccess(externalId);
                  },
                  portfolio,
                })
              }
            />
          ) : (
            <AdminEmptyView
              title={t("contacts.admins.empty-state.title")}
              openInviteNewAdminDialog={(lang) =>
                dispatch([
                  {
                    type: "showInviteNewAdminDialog",
                    lang,
                    companyItems,
                  },
                ])
              }
              showCompanyEmptyState={portfolioStore.allCompanies.length > 0}
              portfolio={portfolio}
            />
          )}
        </>
      );
    } else {
      return undefined;
    }
  }

  function renderCustomRole() {
    if (customRoleItemResult === undefined || isPending(customRoleItemResult)) {
      return (
        <VBox fullheight justifyContent="center">
          <LoadingIndicator />
        </VBox>
      );
    }
    if (store !== undefined) {
      return <CustomRoleView store={store} />;
    }
    return undefined;
  }

  return (
    <FullScreenLayout
      alertStore={messageStore}
      pageHeader={
        <AppBar
          data={appBar}
          subHeaderElement={
            isOk(result) ? (
              <ContactsHeader
                numberOfShareholders={
                  prepareRowData(
                    result.data.shareholders.map(transformShareholder),
                    portfolioStore.allCompanies,
                  ).length
                }
                numberOfAdminsInCompany={admins.length}
                contactsTab={contactsTab}
                portfolio={portfolio}
                roleItems={customRoleItems}
                locale={locale}
              />
            ) : undefined
          }
        />
      }
      centerPanel={
        contactsTab.type === "admins"
          ? renderAdmins()
          : contactsTab.type === "shareholders"
            ? renderShareholders()
            : renderCustomRole()
      }
    />
  );
});

const mapToContactsUrl = (portfolio: SelectedPortfolioOption, tab: PortfolioContactsTab) =>
  tab.type === "custom-role"
    ? `${extractPortfolioContactsUrl(portfolio)}/${tab.type}/${tab.customRoleId}`
    : `${extractPortfolioContactsUrl(portfolio)}/${tab.type}`;

const ContactsHeader = observer(function ContactsHeader({
  numberOfShareholders,
  contactsTab,
  portfolio,
  numberOfAdminsInCompany,
  roleItems,
  locale,
}: {
  numberOfShareholders?: number;
  numberOfAdminsInCompany?: number;
  contactsTab: PortfolioContactsTab;
  portfolio: SelectedPortfolioOption;
  roleItems?: CustomRoleItem[];
  locale: Language;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const tabs: TabProps[] = [
    {
      id: "shareholders",
      label: t("contacts.shareholders", { count: numberOfShareholders }),
      onSelect: () => history.push(mapToContactsUrl(portfolio, { type: "shareholders" })),
      isActive: contactsTab.type === "shareholders",
    },
    {
      id: "admins",
      label: t("contacts.admins", { count: numberOfAdminsInCompany }),
      onSelect: () => history.push(mapToContactsUrl(portfolio, { type: "admins" })),
      isActive: contactsTab.type === "admins",
    },
    ...(roleItems
      ? roleItems
          .filter((f) => f.companies.length > 1)
          .map<TabProps>((roleItem) => ({
            id: roleItem.role.customRoleId,
            key: roleItem.role.customRoleId,
            label: `${getRoleNameByLanguage(roleItem.role, locale)} (${prepCustomRoleUsersTableData({ users: roleItem.availableUsers, companies: roleItem.companies }).length})`,
            onSelect: () =>
              history.push(
                mapToContactsUrl(portfolio, {
                  type: "custom-role",
                  customRoleId: roleItem.role.customRoleId,
                }),
              ),
            isActive:
              contactsTab.type === "custom-role" &&
              contactsTab.customRoleId === roleItem.role.customRoleId,
          }))
      : []),
  ];
  return (
    <Box bg>
      <VBox maxWidth="xl" container marginTop="lg">
        <h4>{t("header.contacts")}</h4>
        <Tabs tabs={tabs} />
      </VBox>
    </Box>
  );
});

function ContactDropdownMenu({
  row,
  onNavigateToShareholder,
}: {
  row: ContactRow;
  onNavigateToShareholder: (shareholderId: number) => void;
}) {
  const history = useRootStore().history;
  const { t } = useTranslation();

  const companyItems: DropdownMenuItemType[] = row.companies.map((c) => ({
    type: "item",
    label: c.name,
    icon: "eye",
    onSelect: () => navigateToAdminDashboard(c.orgNo, history),
  }));
  return (
    <DropdownMenu
      button={{ type: "dotdotdot" }}
      alignment={"end"}
      items={[
        {
          type: "item",
          label: t("portfolioContacts.viewShareholder"),
          icon: "user-list",
          onSelect: () => onNavigateToShareholder(row.shareholderId),
        },
        { type: "label", label: t("portfolioContacts.goToCompany") },
        ...companyItems,
      ]}
    />
  );
}

function AdminDropdownMenu({
  onNavigateToAdmin,
  openEditEmail,
  openRevokeAll,
}: {
  onNavigateToAdmin: () => void;
  openEditEmail: () => void;
  openRevokeAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      button={{ type: "dotdotdot" }}
      alignment={"end"}
      items={[
        {
          type: "item",
          label: t("contacts.admins.manage-permissions"),
          icon: "user-list",
          onSelect: () => onNavigateToAdmin(),
        },
        {
          type: "item",
          label: t("contacts.admins.edit-email"),
          icon: "pen",
          onSelect: () => openEditEmail(),
        },
        {
          type: "item",
          label: t("contacts.admins.revoke-all"),
          icon: "trash",
          color: "error",
          onSelect: () => openRevokeAll(),
        },
      ]}
    />
  );
}

const AdminsView = observer(function AdminsView({
  admins,
  adminsSearch,
  companyFilter,
  openInviteNewAdminDialog,
  navigateToAdminView,
  openEmailEdit,
  openRevokeAll,
}: {
  admins: AdminItem[];
  adminsSearch: StringFieldModel;
  companyFilter?: RadioFieldModel<NamedOrganisationNumber | null>;
  openInviteNewAdminDialog: (language: Language) => void;
  navigateToAdminView: (externalId: string) => void;
  openEmailEdit: (externalId: string, email: string, adminName: string) => void;
  openRevokeAll: (orgNos: string[], externalId: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const columns: TableColumn<AdminItem>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
    },
    {
      name: "companies",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => <ContactAdminsCompaniesData companies={row.companies} />,
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
    },
    {
      name: "menu",
      label: "",
      align: "right",
      getValue: (row) => (
        <AdminDropdownMenu
          onNavigateToAdmin={() => navigateToAdminView(row.id)}
          openEditEmail={() => openEmailEdit(row.id, row.email, row.name)}
          openRevokeAll={() =>
            openRevokeAll(
              row.companies.map((a) => a.organisationNumber),
              row.id,
              row.name,
            )
          }
        />
      ),
    },
  ];
  return (
    <Container padding maxWidth="xl">
      <Box vertical={isMobileView()}>
        <Box vertical={isMobileView()}>
          <MTextInput
            model={adminsSearch}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
          />
          {companyFilter && (
            <Box>
              <MSelect model={companyFilter} />
            </Box>
          )}
        </Box>
        <Box marginLeft={isMobileView() ? false : "auto"}>
          <Button
            variant={"secondary"}
            label={t("contacts.admins.invite-new-admin")}
            icon={"plus"}
            onClick={() => openInviteNewAdminDialog(locale)}
          />
        </Box>
      </Box>
      <DataTable
        onRowClick={(row) => navigateToAdminView(row.id)}
        variant={"LARGE"}
        tableColumns={columns}
        tableData={filterAdminRows(admins, adminsSearch.inputValue, companyFilter?.value)}
        getId={(row) => row.id}
      />
    </Container>
  );
});

const ContactsView = observer(function ContactsView({
  shareholders,
  shareholderNameFilter,
  companyFilter,
  shareholderStatusFilter,
  setShareholderStatusFilter,
  onNavigateToShareholder,
  allCompanies,
  downloadShareholdersExcelFile,
  uploadShareholderExcelForUpdate,
  importExcelConfig,
  isSuperAdmin,
}: {
  shareholders: ExtendedShareholderDTO[];
  shareholderNameFilter: StringFieldModel;
  companyFilter?: RadioFieldModel<CompanyItem | null>;
  shareholderStatusFilter: ShareholderStatus;
  setShareholderStatusFilter: (s: ShareholderStatus) => void;
  onNavigateToShareholder: (shareholderId: number) => void;
  allCompanies: PortfolioCompany[];
  downloadShareholdersExcelFile: () => void;
  uploadShareholderExcelForUpdate: (files: File[] | null) => void;
  importExcelConfig?: FileUploadConfig;
  isSuperAdmin?: boolean;
}) {
  const { t } = useTranslation();
  const filesInputRef = useRef<HTMLInputElement>(null);
  const columns: TableColumn<ContactRow>[] = [
    {
      name: "name",
      label: t("portfolioContacts.shareholder"),
      align: "left",
    },
    {
      name: "representative",
      label: t("portfolioContacts.representative"),
      align: "left",
    },
    {
      name: "organizationNumbers",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => <ContactCompaniesData companies={row.companies} />,
      getSortValue: ({ companies }) => companies.map((c) => c.name),
    },
    {
      name: "",
      label: t("portfolioContacts.last-login"),
      align: "left",
      getValue: (row) => <LastLogin {...row} />,
      getSortValue: (row) => (row.lastLogin ? toDate(row.lastLogin).getTime() : 0),
    },
    {
      name: "menu",
      label: "",
      align: "right",
      getValue: (row) => (
        <ContactDropdownMenu row={row} onNavigateToShareholder={onNavigateToShareholder} />
      ),
    },
  ];
  return (
    <Container padding maxWidth="xl">
      <Box>
        <Box>
          <MTextInput
            model={shareholderNameFilter}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
          />
        </Box>
        {companyFilter && (
          <Box>
            <MSelect model={companyFilter} />
          </Box>
        )}
        <Box>
          <Select
            {...prepShareholderStatusFilter(shareholderStatusFilter, setShareholderStatusFilter)}
          />
        </Box>
        {isSuperAdmin && importExcelConfig && (
          <Box grow justifyContent={"end"} alignItems={"center"}>
            <input
              type="file"
              ref={filesInputRef}
              className={"tw-hidden"}
              accept={getSupportedExtensions(importExcelConfig.supportedFileTypes, {
                leadingDot: true,
              }).toString()}
              onChange={(event) => {
                uploadShareholderExcelForUpdate(
                  event.target.files ? Array.from(event.target.files) : [],
                );
              }}
              onClick={(event) => {
                (event.target as HTMLInputElement).value = "";
              }}
            />
            <DropdownMenu
              button={{ type: "link", label: t("shareholders.excel.edit") }}
              items={[
                {
                  type: "item",
                  icon: "download",
                  label: { key: "shareholders.excel.download" },
                  onSelect: downloadShareholdersExcelFile,
                },
                {
                  type: "item",
                  icon: "upload",
                  label: { key: "shareholders.excel.upload" },
                  onSelect: () => filesInputRef.current?.click(),
                },
                {
                  type: "custom-item",
                  canSelect: false,
                  render: () => (
                    <VBox padding={"xs"} className={"tw-w-96"}>
                      <Alert
                        type={"info"}
                        message={<Markdown value={t("shareholders.excel.info")} />}
                        icon={"info"}
                      />
                    </VBox>
                  ),
                },
              ]}
            />
          </Box>
        )}
      </Box>
      <DataTable
        onRowClick={(row) => onNavigateToShareholder(row.shareholderId)}
        tableColumns={columns}
        tableData={prepareAndFilterContactRows(
          allCompanies,
          shareholders,
          shareholderStatusFilter,
          shareholderNameFilter.inputValue,
          companyFilter?.value,
        )}
        getId={(row) => row.id}
        initialSortBy={"name"}
        initialSortDirection={"asc"}
      />
    </Container>
  );
});

function ContactCompaniesData({ companies }: { companies: ContactCompany[] }) {
  const { t } = useTranslation();
  const language = useLanguage();
  return companies.length === 1 ? (
    <Box>
      <div>{companies[0].name}</div>
      <Box fg={"subtle"}>{formatPercent(language, companies[0].ownershipPercentage)}</Box>
    </Box>
  ) : (
    <Box>
      <HoverTooltip
        content={
          <ul>
            {companies.map((c) => (
              <Box key={c.orgNo} whitespace={"nowrap"}>
                <div>{c.name}</div>
                <Box fg={"subtle"}>{formatPercent(language, c.ownershipPercentage)}</Box>
              </Box>
            ))}
          </ul>
        }
      >
        {({ targetRef }) => (
          <Box ref={targetRef} className={"underline tw-decoration-dotted"}>
            {t("portfolio.numberOfCompanies", { count: companies.length })}
          </Box>
        )}
      </HoverTooltip>
    </Box>
  );
}

function ContactAdminsCompaniesData({ companies }: { companies: CompanyItem[] }) {
  const { t } = useTranslation();
  return companies.length === 1 ? (
    <Box>
      <div>{companies[0].companyName}</div>
    </Box>
  ) : (
    <Box>
      <HoverTooltip
        content={
          <div>
            {companies.map((c) => (
              <Box key={c.organisationNumber} whitespace={"nowrap"}>
                <div>{c.companyName}</div>
              </Box>
            ))}
          </div>
        }
      >
        {({ targetRef }) => (
          <Box ref={targetRef} className={"underline tw-decoration-dotted"}>
            {t("portfolio.numberOfCompanies", { count: companies.length })}
          </Box>
        )}
      </HoverTooltip>
    </Box>
  );
}

export const InviteNewAdminDialog = observer(function InviteNewAdminDialog({
  store,
}: {
  store: InviteNewAdminDialogStore;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("invite-user.title")}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t("cancel")}
      onSubmit={() => store.submitInviteNewAdminDialog()}
      onCancel={store.onClose}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <VBox gap={"md"}>
        <MTextInput label={t(`invite-user.email`)} model={store.email} />
        <MSelect model={store.language} label={t("invite-user.choose-language-for-email")} />
        <Box>
          <LinkButton label={t("select-all")} onClick={() => store.toggleSelectAllCompanies()} />
          {"/"}
          <LinkButton
            label={t("portfolio.NONE")}
            onClick={() => store.deselectAllCompaniesToggle()}
          />
        </Box>
        {store.availableCompaniesInPortfolio.map((c) => (
          <Checkbox
            key={c.organisationNumber}
            label={
              <Box>
                <div>{c.name}</div>
                <small className={"subtle"}>{c.organisationNumber}</small>
              </Box>
            }
            value={c.organisationNumber}
            checked={store.isSelected(c.organisationNumber)}
            onChange={() => store.toggleSelected(c.organisationNumber)}
          />
        ))}
      </VBox>
    </Dialog>
  );
});

export const AdminEmptyView = observer(
  ({
    title,
    description,
    openInviteNewAdminDialog,
    showCompanyEmptyState,
    portfolio,
  }: {
    title: string;
    description?: string;
    openInviteNewAdminDialog: (language: Language) => void;
    showCompanyEmptyState: boolean;
    portfolio: SelectedPortfolioOption;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const history = useRootStore().history;
    return (
      <VBox grow={"golden"} justifyContent={"center"}>
        {showCompanyEmptyState ? (
          <EmptyState
            illustration={"briefcase"}
            title={t("portfolio.empty-active-companies.title")}
            description={description}
            actions={
              <Button
                variant={"secondary"}
                label={t("contacts.admins.empty-state.add-to-portfolio")}
                onClick={() => navigateToAdminCompanyPortfolios(portfolio, history)}
              />
            }
          />
        ) : (
          <EmptyState
            illustration={"user-list"}
            title={title}
            description={description}
            actions={
              <Button
                variant={"secondary"}
                label={t("contacts.admins.invite-new-admin")}
                icon={"plus"}
                onClick={() => openInviteNewAdminDialog(locale)}
              />
            }
          />
        )}
      </VBox>
    );
  },
);

export const AdminInfoPage = observer(
  ({
    externalId,
    portfolioType,
    portfolioId,
    activeHeader,
  }: {
    portfolioType: PortfolioType;
    externalId?: string;
    portfolioId?: string;
    activeHeader: string | undefined;
  }) => {
    const { portfolio } = usePortfolio({ activeHeader, portfolioType, portfolioId });
    const { portfolioStore, messageStore, dispatch, apiService, history, http, userStore } =
      useRootStore();

    useEffect(() => {
      portfolioStore.fetchPortfolioAdmins(portfolio);
      return () => {
        portfolioStore.resetPortfolioAdmins();
      };
    }, [portfolio, portfolioStore]);

    const store = useMemo(
      () =>
        isOk(portfolioStore.fetchPortfolioAdminsResult)
          ? new AdminDialogStore(
              portfolioStore.fetchPortfolioAdminsResult.data.admins.find(
                (a) => a.id === externalId,
              )!,
              () => dispatch([{ type: "closeDialog" }]),
              apiService,
              portfolio,
              messageStore,
              history,
              portfolioStore.fetchPortfolioAdminsResult.data.availableCompanies,
              http,
              userStore.user,
            )
          : undefined,
      [
        apiService,
        dispatch,
        externalId,
        history,
        messageStore,
        portfolio,
        portfolioStore.fetchPortfolioAdminsResult,
        http,
        userStore.user,
      ],
    );

    const renderEditDialog = () => {
      switch (store?.editDialogState?.type) {
        case "resendInvite":
          return <SendInvitationDialog store={store.editDialogState.form} />;
        case "revokeAccess":
          return <RevokeAccessInAdminForm store={store.editDialogState.form} />;
        case "addAdditionalCompany":
          return <AddCompanyForm store={store.editDialogState.form} />;
        default:
          return null;
      }
    };
    return (
      <FullScreenLayout
        pageHeader={<AdminInfoHeader selectedPortfolio={portfolio} adminName={store?.admin.name} />}
        centerPanel={
          store === undefined ? (
            <LoadingIndicator />
          ) : (
            <>
              <AdminInfoBody
                admin={store.admin}
                store={store}
                onSuccess={() => portfolioStore.onRevokeAllAdminAccess(store.admin.id)}
                portfolio={portfolio}
              />
              {renderEditDialog()}
            </>
          )
        }
      />
    );
  },
);

const AdminInfoHeader = observer(function AdminInfoHeader({
  selectedPortfolio,
  adminName,
}: {
  selectedPortfolio: SelectedPortfolioOption;
  adminName?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Box
      bg={"default"}
      grow
      alignItems={"center"}
      paddingLeft={"md"}
      paddingRight={"lg"}
      paddingY={"md"}
      borderBottom
    >
      <IconButton
        icon={"x"}
        tooltip={t("back")}
        onClick={() => dispatch([navigate(extractPortfolioContactsAdminUrl(selectedPortfolio))])}
      />
      <div>{adminName}</div>
      <div>-</div>
      <div>{t(selectedPortfolio.label)}</div>
    </Box>
  );
});

const AdminInfoBody = observer(function AdminInfoBody({
  admin,
  store,
  onSuccess,
  portfolio,
}: {
  admin: AdminItem;
  store: AdminDialogStore;
  onSuccess: () => void;
  portfolio: SelectedPortfolioOption;
}) {
  const { t } = useTranslation();
  const { openEditEmailDialog, openRevokeAllDialog } = usePortfolioStore();
  return (
    <VBox maxWidth="lg" gap={"md"} container marginTop="lg">
      <h2>{admin.name}</h2>
      <Card title={t("contacts.admins.details")} alignItems={"start"}>
        <Grid>
          <GridItem xs={12} lg={4}>
            {t("email")}
          </GridItem>
          <GridItem xs={12} lg={8}>
            <Box>
              <div>{admin.email}</div>
              <LinkButton
                label={t("edit")}
                icon={"edit"}
                onClick={() =>
                  openEditEmailDialog({
                    id: admin.id,
                    email: admin.email,
                    onSuccess: (updateEmail) => store.onSuccessUpdateEmail(updateEmail),
                    name: admin.name,
                  })
                }
              />
            </Box>
          </GridItem>
        </Grid>
      </Card>
      <AdminInCompaniesCard
        store={store}
        openRevokeAllDialog={() =>
          openRevokeAllDialog({
            orgNos: admin.companies.map((f) => f.organisationNumber),
            externalId: admin.id,
            name: admin.name,
            onSuccess,
            portfolio,
          })
        }
      />
    </VBox>
  );
});

const AdminInCompaniesCard = observer(
  ({
    store,
    openRevokeAllDialog,
  }: {
    store: AdminDialogStore;
    openRevokeAllDialog: () => void;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <Card
        title={
          store.admin.companies.length > 1
            ? t("contacts.admins.title", {
                n: store.admin.companies.length,
              })
            : t("contacts.admins.title-one")
        }
        action={
          store.admin.companies.length > 1 ? (
            <LinkButton
              color={"error"}
              label={t("contacts.admins.revoke-all")}
              onClick={() => openRevokeAllDialog()}
            />
          ) : undefined
        }
      >
        {store.admin.companies.map((c) => (
          <Fragment key={c.organisationNumber}>
            <Box vertical={isMobileView()} marginTop={"md"}>
              <div className={"grow"}>{c.companyName}</div>
              <Box gap={"md"} alignItems={"end"}>
                <LinkButton
                  label={t("contacts.admins.resend-invite")}
                  icon={"paper-plane-tilt"}
                  onClick={() => store.openResendInviteDialog(locale, c, c.organisationNumber)}
                />
                <LinkButton
                  label={t("contacts.admins.revoke-access")}
                  icon={"trash"}
                  onClick={() => {
                    store.openRevokeAccessDialog(c.organisationNumber, c.companyName, () =>
                      navigateToAdminCompanyPortfolios(
                        store.selectedPortfolioOption,
                        store.history,
                      ),
                    );
                  }}
                />
              </Box>
            </Box>
            <hr />
          </Fragment>
        ))}
        <Box marginTop={"md"}>
          <LinkButton
            label={t("portfolio.welcome.add-company")}
            icon={"plus"}
            onClick={() => store.openAddAdditionalCompanyDialog(locale)}
          />
        </Box>
      </Card>
    );
  },
);

export const EmailEditForm = observer(({ store }: { store: EditEmailForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("contacts.admins.edit-email-title", { name: store.adminName })}
      cancelLabel={t("cancel")}
      onCancel={store.closeEditDialog}
      submitLabel={t("save")}
      onSubmit={() => store.updateAdministratorEmail()}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <MTextInput model={store.email} label={t("email")} autoSelect />
    </Dialog>
  );
});

const RevokeAccessInAdminForm = observer(({ store }: { store: RevokeAccessForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("settings.revokeAdminAccess")}
      submitLabel={t("confirm")}
      onSubmit={() => store.confirmRevokeAccessAdmin()}
      onCancel={() => store.closeRevokeAccessDialog()}
      isSubmitting={store.isLoading}
    >
      <div>
        {t("settings.revokeAdminAccessForQuestion", {
          name: store.name,
        })}
      </div>
    </Dialog>
  );
});

export const RevokeAllInAdminForm = observer(({ store }: { store: RevokeAllForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("settings.revokeAdminAccess")}
      submitLabel={t("confirm")}
      onSubmit={() => store.confirmRevokeAll()}
      onCancel={() => store.closeRevokeAccessDialog()}
      isSubmitting={store.isLoading}
    >
      <div>{t("settings.revokeAdminAccessForQuestion", { name: store.name })}</div>
    </Dialog>
  );
});

const AddCompanyForm = observer(({ store }: { store: AddAdditionalCompanyForm }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      title={t("contacts.admins.add-additional-company-title", { name: store.admin.name })}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t("cancel")}
      onSubmit={() => store.submitAddCompanyInAdminDialog()}
      onCancel={store.onClose}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <>
        {store.isAlreadyAdminInAllCompanies ? (
          <div>
            {t({
              key: "contacts.admins.already-admin-in-all-companies-in-portfolio",
              params: {
                name: store.admin.name,
                portfolio: store.selectedPortfolioOption.label,
              },
            })}
          </div>
        ) : (
          <VBox gap={"md"}>
            <Box>
              <LinkButton label={t("select-all")} onClick={() => store.selectAllCompanies()} />
              {"/"}
              <LinkButton
                label={t("portfolio.NONE")}
                onClick={() => store.deselectAllCompanies()}
              />
            </Box>
            {store.companyOptions.map((c) => (
              <Checkbox
                key={c.organisationNumber}
                label={
                  <Box>
                    <div>{c.companyName}</div>
                    <small className={"subtle"}>{c.organisationNumber}</small>
                  </Box>
                }
                value={c.organisationNumber}
                checked={store.isSelected(c.organisationNumber)}
                onChange={() => store.toggleSelected(c.organisationNumber)}
              />
            ))}
            <MSelect model={store.language} label={t("invite-user.choose-language-for-email")} />
          </VBox>
        )}
      </>
    </Dialog>
  );
});
